import { Button } from '@heroui/button';
import { Card, CardBody, CardHeader } from '@heroui/card';
import { Divider } from '@heroui/divider';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { useAuth } from '../firebase/useAuth.ts';

export const Route = createFileRoute('/login')({
  component: LoginPage,
});

function LoginPage() {
  const { signInWithSAML, currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(() => {
    // Check for any stored SAML errors on initial render
    const storedError = sessionStorage.getItem('saml_error');
    if (storedError) {
      sessionStorage.removeItem('saml_error');
      return storedError;
    }
    return null;
  });

  // Redirect if user is already logged in
  useEffect(() => {
    console.log('Login page useEffect - currentUser:', currentUser);
    if (currentUser) {
      console.log('User is logged in, navigating to dashboard');
      navigate({ to: '/' });
    }
  }, [currentUser, navigate]);

  const handleSAMLSignIn = async () => {
    try {
      setLoading(true);
      setError(null);
      await signInWithSAML();

      // Navigation will happen after redirect returns
      // No need to navigate here as the redirect will handle it
    } catch (error) {
      console.error('Login failed:', error);
      setError(error instanceof Error ? error.message : 'Login failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-background to-secondary-50 p-4">
      <div className="absolute inset-0 bg-grid-small-black/[0.02] pointer-events-none" />
      <Card className="w-full max-w-md shadow-xl backdrop-blur-md bg-background/95">
        <CardHeader className="flex flex-col items-center pt-8 pb-0">
          <div className="relative mb-4">
            <img src="/logo-full.svg" alt="Relay Logo" className="w-16 h-16" />
          </div>
          <h1 className="text-3xl font-bold bg-gradient-to-br from-foreground to-foreground-600 bg-clip-text text-transparent">
            Relay
          </h1>
        </CardHeader>

        <CardBody className="px-8 py-6">
          <div className="space-y-6">
            {error && (
              <Card className="bg-danger-50 border-none">
                <CardBody className="py-3 px-4">
                  <p className="text-danger-600 text-sm font-medium">{error}</p>
                </CardBody>
              </Card>
            )}

            <Divider className="my-4" />

            <div className="space-y-3">
              <p className="text-sm text-foreground-500 text-center">Sign in with your organization account</p>

              <Button
                color="primary"
                size="lg"
                className="w-full font-medium"
                variant="shadow"
                onPress={handleSAMLSignIn}
                isLoading={loading}
                startContent={
                  !loading && (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                  )
                }
              >
                {loading ? 'Redirecting to Google Workspace...' : 'Sign in with Google Workspace'}
              </Button>
            </div>

            <Divider className="my-4" />

            <div className="space-y-3">
              <div className="flex items-center justify-center space-x-2 text-xs text-foreground-400">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                <span>Secure SSO Authentication</span>
              </div>

              <p className="text-xs text-center text-foreground-400">
                By signing in, you agree to our Terms of Service and Privacy Policy
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      <div className="absolute bottom-4 text-center">
        <p className="text-xs text-foreground-400">© 2024 Relay. All rights reserved.</p>
      </div>
    </div>
  );
}
